<template>
  <div class="settings-page">
    <div class="header-with-dropdown">
      <div>
        <h2>Types</h2>
        <div class="subtitle">
          Create your own types and start collecting information.
        </div>
      </div>
      <UpgradeButton
        v-if="!hasMultipleWorkspaces"
        entitlement-key="databases"
        :organization-id="orgId"
        :entitled="isEntitledToCreateCustomDatabase(workspaceId)"
        kind="main"
        type="primary"
        text="Create"
        @click="navigate(workspaceId)"
      >
        <template #icon>
          <AddIcon />
        </template>
      </UpgradeButton>
    </div>
    <section class="types-block">
      <div
        v-for="workspace in workspaceTypesList"
        :key="workspace.id"
        class="workspace-section"
      >
        <button
          v-if="!isPersonalWorkspace(workspace.id)"
          class="workspace-header"
          @click="toggleWorkspace(workspace.id)"
        >
          <ChevronRightIcon
            class="chevron-icon"
            :class="{ expanded: !collapsedWorkspaces[workspace.id] }"
          />
          <span class="workspace-name">{{ workspace.name }}</span>
          <span class="type-count">{{ workspace.types.length }}</span>
        </button>

        <div
          v-if="
            isPersonalWorkspace(workspace.id) ||
            !collapsedWorkspaces[workspace.id]
          "
          class="workspace-content"
          :class="{ 'no-indent': isPersonalWorkspace(workspace.id) }"
        >
          <div v-if="workspace.types.length > 0" class="settings-table">
            <div class="settings-table-header types-row">
              <span class="col-header column-1">Type</span>
              <span class="col-header column-2">Nature</span>
              <span class="col-header column-3">Objects</span>
              <span class="col-header column-4">Properties</span>
              <span class="col-header column-5"></span>
            </div>
            <template
              v-for="(type, typeIndex) in workspace.types"
              :key="type.id"
            >
              <div v-if="typeIndex > 0" class="row-separator" />
              <div
                class="types-row data-row"
                role="button"
                tabindex="0"
                @click="openDatabase(workspace.id, type.id, $event)"
              >
                <div class="table-cell with-icon column-1">
                  <BaseType
                    :title="type.data_name"
                    :image="type.defaultIcon"
                    :color="getTypeColor(type)"
                    state="img"
                    size="s"
                    :clickable="true"
                  />
                  <span>{{ type.data_name }}</span>
                  <span
                    v-if="type.hasDuplicateFields || type.hasDuplicateName"
                    class="warning-icon-wrapper"
                    :title="warningTitle(type)"
                  >
                    <TriangleWarningIcon class="warning-icon" />
                  </span>
                </div>
                <div class="table-cell column-2">
                  <span class="badge" :class="type.nature">{{
                    natureLabel(type.nature)
                  }}</span>
                </div>
                <div class="table-cell secondary column-3">
                  {{ type.objectCount ?? "—" }}
                </div>
                <div class="table-cell secondary column-4">
                  {{ type.propertyCount ?? "—" }}
                </div>
                <div class="table-cell column-5" @click.stop>
                  <UpgradeButton
                    v-if="isTypeEditable(workspace.id, type)"
                    entitlement-key="databases"
                    :organization-id="orgId"
                    :entitled="isTypeEditable(workspace.id, type)"
                    kind="auxiliary"
                    text="Edit"
                    @click="navigateToEdit(workspace.id, type.id, $event)"
                  />
                  <BaseButton
                    v-else
                    kind="auxiliary"
                    text="Edit"
                    state="disabled"
                    :tooltip="{
                      text: 'Only the first custom types allowed by your plan can be edited; remove or upgrade to change others.',
                    }"
                    tooltip-display="always"
                  />
                </div>
              </div>
            </template>
          </div>
          <div v-if="hasMultipleWorkspaces" class="create-button-footer">
            <UpgradeButton
              entitlement-key="databases"
              :organization-id="orgId"
              :entitled="isEntitledToCreateCustomDatabase(workspace.id)"
              kind="main"
              type="primary"
              text="Create"
              @click="navigate(workspace.id)"
            >
              <template #icon>
                <AddIcon />
              </template>
            </UpgradeButton>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>
<script lang="ts">
import { computedAsync } from "@vueuse/core";
import {
  Component,
  computed,
  defineComponent,
  reactive,
  ref,
  toRefs,
  watch,
} from "vue";

import { GraphDatabase, WorkspaceId } from "@ctrl/controller-rpc";

import BaseButton from "@/components/base/BaseButton.vue";
import BaseType from "@/components/base/BaseType.vue";
import UpgradeButton from "@/components/base/UpgradeButton.vue";
import {
  getTypeColor,
  getTypeIcon,
  isBuiltinDatabase,
} from "@/components/databases";
import { graphRecordToProperties } from "@/components/graph/component";
import { getGraphQuery, runCompiledQuery } from "@/components/graph/query";
import AddIcon from "@/components/icons/AddIcon.vue";
import ChevronRightIcon from "@/components/icons/ChevronRightIcon.vue";
import TriangleWarningIcon from "@/components/icons/TriangleWarningIcon.vue";
import * as inject from "@/components/inject";
import { bindAsync, releasableRef, useReleaser } from "@/ref";
import { navigateToDatabase, navigateToType } from "@/utils/navigate";

type TypeNature = "system" | "custom" | "integration";

type Type = GraphDatabase & {
  defaultIcon: Component | string;
  objectCount?: number;
  propertyCount?: number;
  nature: TypeNature;
  hasDuplicateFields?: boolean;
  hasDuplicateName?: boolean;
};

const natureLabels: Record<TypeNature, string> = {
  system: "Default",
  custom: "Custom",
  integration: "Integration",
};

const natureLabel = (nature: TypeNature): string => natureLabels[nature];

const databaseNature = (database: GraphDatabase): TypeNature => {
  if (database.origin === "integration_app") return "integration";
  return isBuiltinDatabase(database) ? "system" : "custom";
};

// Display order of the natures in each workspace's type list.
const natureRank: Record<TypeNature, number> = {
  custom: 0,
  system: 1,
  integration: 2,
};

type WorkspaceWithTypes = {
  id: WorkspaceId;
  name: string;
  types: Type[];
};

export default defineComponent({
  components: {
    BaseButton,
    BaseType,
    AddIcon,
    ChevronRightIcon,
    TriangleWarningIcon,
    UpgradeButton,
  },
  props: {
    orgId: { type: String, required: true },
    workspaceId: { type: String, required: true },
  },
  async setup(props) {
    const { orgId, workspaceId } = toRefs(props);
    inject.currentWorkspaceId(workspaceId);
    const router = inject.router();
    const controller = inject.controller();
    const profile = inject.profile();
    const billing = inject.billing();
    const workspaceDatabases = inject.workspaceDatabases();
    const isBetaTester = inject.isBetaTester();

    const databasesEntitlement = computed(() => {
      return (
        billing.value?.getEntitlement("databases", orgId.value)?.value ??
        undefined
      );
    });
    const numberOfDatabasesEntitledTo = computed(() => {
      if (databasesEntitlement.value === undefined) return undefined;
      if (databasesEntitlement.value.value === "∞")
        return Number.MAX_SAFE_INTEGER;
      return Number(databasesEntitlement.value.value) || 0;
    });
    const getPropertyCount = (database: GraphDatabase): number =>
      database.type ? graphRecordToProperties(database.type).length : 0;

    const hasDuplicateFieldNames = (database: GraphDatabase): boolean => {
      let result = false;
      if (database.type) {
        const properties = graphRecordToProperties(database.type);
        const nameCount = new Map<string, number>();
        properties.forEach((prop) => {
          const count = nameCount.get(prop.name) || 0;
          nameCount.set(prop.name, count + 1);
        });
        result = Array.from(nameCount.values()).some((count) => count > 1);
      }
      return result;
    };

    const warningTitle = (type: Type): string => {
      const problems: string[] = [];
      if (type.hasDuplicateName) {
        problems.push("Another type in this workspace has the same name.");
      }
      if (type.hasDuplicateFields) {
        problems.push("This type has duplicate property names.");
      }
      return problems.join(" ");
    };

    const getObjectCount = async (database: GraphDatabase): Promise<number> => {
      const query = await getGraphQuery(
        database.workspace,
        `length(${database.name})`,
        controller,
        { databaseName: { type: "const", value: database.name } }
      );
      let count = 0;
      if (query) {
        const result = await runCompiledQuery(query, controller);
        count = typeof result.value === "bigint" ? Number(result.value) : 0;
        result.release();
      }
      return count;
    };

    const personalWorkspaceId = computed(
      () => profile.value.user.personal.workspaceId
    );

    const isPersonalWorkspace = (wsId: string) =>
      wsId === personalWorkspaceId.value;

    const collapsedWorkspaces = reactive<Record<string, boolean>>({});

    const toggleWorkspace = (wsId: string) => {
      collapsedWorkspaces[wsId] = !collapsedWorkspaces[wsId];
    };

    const availableWorkspacesRef = ref<
      Array<{ id: WorkspaceId; name: string }>
    >([]);

    const workspaceTypesList = computedAsync<WorkspaceWithTypes[]>(async () => {
      const wdb = workspaceDatabases.value ?? {};
      const workspaces = availableWorkspacesRef.value ?? [];

      const results = await Promise.all(
        workspaces
          .filter((ws) => wdb[ws.id])
          .map(async (ws) => {
            const databases = wdb[ws.id];
            const types = await Promise.all(
              Object.values(databases)
                // Integration databases cannot be edited: only beta testers
                // see them.
                .filter(
                  (database) =>
                    isBetaTester.value || database.origin !== "integration_app"
                )
                .sort((a, b) => {
                  const rankDiff =
                    natureRank[databaseNature(a)] -
                    natureRank[databaseNature(b)];
                  if (rankDiff !== 0) return rankDiff;
                  return a.data_name.localeCompare(b.data_name);
                })
                .map(async (database: GraphDatabase) => {
                  const objectCount = await getObjectCount(database);
                  const propertyCount = getPropertyCount(database);
                  return {
                    ...database,
                    defaultIcon: getTypeIcon(profile.value, database),
                    objectCount,
                    propertyCount,
                    nature: databaseNature(database),
                    hasDuplicateFields: hasDuplicateFieldNames(database),
                  };
                })
            );

            // Flag types whose name collides with another type in the same
            // workspace (the backend rejects this at save time with
            // `duplicate_data_name`, but existing collisions still surface
            // here so they can be fixed).
            const nameCounts = new Map<string, number>();
            types.forEach((t) => {
              nameCounts.set(
                t.data_name,
                (nameCounts.get(t.data_name) ?? 0) + 1
              );
            });
            const typesWithDuplicateNames = types.map((t) => ({
              ...t,
              hasDuplicateName: (nameCounts.get(t.data_name) ?? 0) > 1,
            }));

            return { id: ws.id, name: ws.name, types: typesWithDuplicateNames };
          })
      );

      return results;
    }, []);

    /** Custom type ids per workspace, in UI order (custom sorted by name). */
    const customTypeIdsByWorkspace = computed(() => {
      const ids = new Map<string, string[]>();
      for (const ws of workspaceTypesList.value ?? []) {
        ids.set(
          ws.id,
          ws.types.filter((t) => t.nature === "custom").map((t) => t.id)
        );
      }
      return ids;
    });

    // The databases entitlement is applied per workspace: each workspace can
    // hold up to the entitled number of custom databases.
    const isEntitledToCreateCustomDatabase = (wsId: string): boolean => {
      const limit = numberOfDatabasesEntitledTo.value;
      if (limit === undefined) return false;
      const count = customTypeIdsByWorkspace.value.get(wsId)?.length ?? 0;
      return count < limit;
    };

    const isCustomDatabaseEditable = (
      wsId: string,
      typeId: string
    ): boolean => {
      const limit = numberOfDatabasesEntitledTo.value;
      if (limit === undefined) return false;
      if (limit >= Number.MAX_SAFE_INTEGER / 2) return true;
      const order = customTypeIdsByWorkspace.value.get(wsId) ?? [];
      const idx = order.indexOf(typeId);
      if (idx === -1) return false;
      return idx < limit;
    };

    // Integration types are always editable: they are only shown to beta
    // testers, and don't count against the custom-databases entitlement.
    const isTypeEditable = (
      wsId: string,
      type: { nature: TypeNature; id: string }
    ) => type.nature !== "custom" || isCustomDatabaseEditable(wsId, type.id);

    const releaser = useReleaser();
    const availableWorkspaces = await (async () => {
      const workspaceIds = releaser.add(
        bindAsync(orgId, async (id) =>
          id ? controller.organization.workspaces.all(id) : releasableRef([])
        )
      );

      const availableWorkspaces = releaser.add(
        workspaceIds.then((ids) =>
          bindAsync(ids, async (ids) => {
            if (!ids?.length)
              return releasableRef<Array<{ id: WorkspaceId; name: string }>>(
                []
              );

            const wsPromises = ids.map(async (wsId: string) => {
              const workspaceRef = await controller.workspace.get(wsId);
              const workspace = workspaceRef.value;
              workspaceRef.release();
              if (!workspace) return null;
              return {
                id: workspace.id,
                name: workspace.name || `Workspace ${wsId.slice(-8)}`,
              };
            });

            const loadedWorkspaces = await Promise.all(wsPromises);
            return releasableRef(
              loadedWorkspaces.filter(
                (ws): ws is { id: WorkspaceId; name: string } => ws !== null
              )
            );
          })
        )
      );

      return availableWorkspaces;
    })();

    releaser.defer(
      watch(
        () => availableWorkspaces.value,
        (newValue) => {
          availableWorkspacesRef.value = newValue || [];
        },
        { immediate: true }
      )
    );

    const navigate = (wsId: string, id?: string) => {
      if (id) {
        navigateToType(router, orgId.value, wsId, id);
      } else {
        navigateToType(router, orgId.value, wsId);
      }
    };

    const navigateToEdit = (wsId: string, id: string, event: Event) => {
      event.stopPropagation();
      navigate(wsId, id);
    };

    const openDatabase = async (
      wsId: string,
      databaseId: string,
      event: Event
    ) => {
      event.stopPropagation();
      await navigateToDatabase(router, wsId, databaseId);
    };

    const hasMultipleWorkspaces = computed(
      () => workspaceTypesList.value.length > 1
    );

    return {
      workspaceTypesList,
      hasMultipleWorkspaces,
      navigate,
      navigateToEdit,
      openDatabase,
      getTypeColor,
      collapsedWorkspaces,
      toggleWorkspace,
      isPersonalWorkspace,
      isEntitledToCreateCustomDatabase,
      isTypeEditable,
      natureLabel,
      warningTitle,
    };
  },
});
</script>
<style lang="scss" scoped>
@use "../../styles/settings.scss";

.create-button-footer {
  display: flex;
  margin-top: 8px;
}

.header-with-dropdown {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.types-block {
  display: flex;
  flex-direction: column;
  row-gap: 4px;

  .workspace-section {
    display: flex;
    flex-direction: column;
    margin-bottom: 12px;

    .workspace-header {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 4px;
      border: none;
      background: none;
      border-radius: $border-radius-m;
      transition: background-color 0.15s ease-out;

      @include themed() {
        color: t($primary);
      }

      &:hover {
        @include themed() {
          background-color: t($grey-05);
        }
      }
    }

    .workspace-name {
      @extend %font-body-medium;
      font-size: 15px;
    }

    .workspace-content {
      display: flex;
      flex-direction: column;
      row-gap: 12px;
      padding-inline-start: 30px;
      padding-bottom: 12px;

      &.no-indent {
        padding-inline-start: 0;
      }
    }
  }

  .chevron-icon {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    transition: transform 0.2s ease;

    &.expanded {
      transform: rotate(90deg);
    }
  }

  .type-count {
    @extend %font-body-regular;
    @include themed() {
      color: t($grey-40);
    }
  }

  .types-row {
    display: grid;
    grid-template-columns:
      1fr minmax(0, 120px) minmax(0, 70px) minmax(0, 40px)
      minmax(0, 80px);
    align-items: center;
    column-gap: 8px;

    .column-1 {
      display: flex;
      align-items: center;
      gap: 10px;
      @extend %font-body-regular;
    }

    .column-5 {
      display: flex;
      align-items: flex-end;
      justify-content: flex-end;
    }
  }

  .badge {
    @extend %font-small-regular;
    font-weight: 500;
    letter-spacing: 0.02em;
    padding: 2px 6px;
    border-radius: $border-radius-m;
    text-transform: capitalize;

    &.system {
      @include themed() {
        border: 1px solid t($grey-15);
        color: t($grey-40);
      }
    }

    &.custom {
      @include themed() {
        border: 1px solid t($tertiary-20);
        color: t($tertiary-50);
      }
    }

    &.integration {
      @include themed() {
        border: 1px solid t($secondary-30);
        color: t($secondary-80);
      }
    }
  }

  .warning-icon-wrapper {
    margin-inline-start: 8px;
    cursor: help;
    display: inline-flex;
    align-items: center;
  }

  .warning-icon {
    width: 16px;
    height: 16px;
    color: #f59e0b;
  }
}
</style>
